import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { Races } from '../races/Races.js';
import { BloodParticleSystem } from '../effects/BloodParticles.js';
import { audioManager } from '../audio/AudioManager.js';

export class Ally {
    constructor(scene, world, race, player, pathfinding, enemies, callbacks = {}) {
        this.scene = scene;
        this.world = world;
        this.race = race;
        this.raceData = Races[race];
        this.player = player;
        this.pathfinding = pathfinding;
        this.enemies = enemies; // Reference to enemies array
        this.callbacks = callbacks;
        this.groundBoundary = callbacks.groundBoundary || 48; // Ground boundary to stay within
        
        // Create ally mesh based on race (same as enemy but marked differently)
        this.mesh = this.createAllyMesh();
        this.mesh.position.set(0, 1, 0);
        this.mesh.castShadow = true;
        this.mesh.receiveShadow = true;
        // Mark as ally (NOT enemy) - so player can't shoot them
        this.mesh.userData.isAlly = true;
        this.mesh.userData.allyInstance = this;
        this.scene.add(this.mesh);
        
        // Physics body
        const shape = new CANNON.Box(new CANNON.Vec3(0.5, 1, 0.5));
        this.body = new CANNON.Body({ mass: 1 });
        this.body.addShape(shape);
        this.body.position.set(0, 1, 0);
        this.body.material = new CANNON.Material('ally');
        this.world.addBody(this.body);
        
        this.health = this.raceData.health || 50;
        this.maxHealth = this.health;
        this.moveSpeed = this.raceData.moveSpeed || 2;
        
        this.position = this.mesh.position;
        this.isDead = false;
        this.currentTarget = null; // Current enemy target
        
        // AI state
        this.path = [];
        this.currentPathIndex = 0;
        this.pathUpdateTimer = 0;
        this.pathUpdateInterval = 1.0;
        
        // Blood system
        this.bloodSystem = new BloodParticleSystem(scene);
        this.lastPosition = new THREE.Vector3().copy(this.position);
        this.isWounded = false;
        
        // Attack system
        this.attackRange = 2.5;
        this.attackDamage = this.raceData.attackDamage || 12;
        this.attackCooldown = 1.5;
        this.lastAttackTime = 0;
        this.isAttacking = false;
        
        // Target search
        this.targetSearchTimer = 0;
        this.targetSearchInterval = 0.5; // Search for new target every 0.5 seconds
    }
    
    createAllyMesh() {
        // Reuse the same mesh creation as Enemy but with subtle glow to indicate ally
        const group = new THREE.Group();
        const baseColor = this.raceData.color || 0xff0000;
        
        // Import Enemy's createEnemyMesh logic but with ally glow
        // We'll create the same mesh but with emissive glow
        if (this.race === 'elf') {
            return this.createElfMesh(group, baseColor, true);
        } else if (this.race === 'orc') {
            return this.createOrcMesh(group, baseColor, true);
        } else if (this.race === 'troll') {
            return this.createTrollMesh(group, baseColor, true);
        } else if (this.race === 'wizard') {
            return this.createWizardMesh(group, baseColor, true);
        } else {
            return this.createHumanMesh(group, baseColor, true);
        }
    }
    
    createElfMesh(group, baseColor, isAlly) {
        const material = new THREE.MeshStandardMaterial({ 
            color: baseColor,
            metalness: 0.3,
            roughness: 0.7,
            emissive: isAlly ? 0x00ff00 : 0x000000,
            emissiveIntensity: isAlly ? 0.15 : 0
        });
        
        // Head
        const headGeometry = new THREE.BoxGeometry(0.4, 0.4, 0.4);
        const head = new THREE.Mesh(headGeometry, material);
        head.position.set(0, 1.6, 0);
        group.add(head);
        
        // Pointed ears
        const earGeometry = new THREE.ConeGeometry(0.08, 0.3, 3);
        const leftEar = new THREE.Mesh(earGeometry, material);
        leftEar.rotation.z = Math.PI / 6;
        leftEar.position.set(-0.25, 1.75, 0);
        group.add(leftEar);
        
        const rightEar = new THREE.Mesh(earGeometry, material);
        rightEar.rotation.z = -Math.PI / 6;
        rightEar.position.set(0.25, 1.75, 0);
        group.add(rightEar);
        
        // Body, arms, legs (same as enemy)
        const bodyGeometry = new THREE.BoxGeometry(0.5, 0.8, 0.3);
        const body = new THREE.Mesh(bodyGeometry, material);
        body.position.set(0, 1.0, 0);
        group.add(body);
        
        const armGeometry = new THREE.BoxGeometry(0.15, 0.6, 0.15);
        const leftArm = new THREE.Mesh(armGeometry, material);
        leftArm.position.set(-0.35, 0.9, 0);
        group.add(leftArm);
        
        const rightArm = new THREE.Mesh(armGeometry, material);
        rightArm.position.set(0.35, 0.9, 0);
        group.add(rightArm);
        
        const legGeometry = new THREE.BoxGeometry(0.2, 0.6, 0.2);
        const leftLeg = new THREE.Mesh(legGeometry, material);
        leftLeg.position.set(-0.15, 0.2, 0);
        group.add(leftLeg);
        
        const rightLeg = new THREE.Mesh(legGeometry, material);
        rightLeg.position.set(0.15, 0.2, 0);
        group.add(rightLeg);
        
        // Bow
        const bowGeometry = new THREE.BoxGeometry(0.08, 0.6, 0.08);
        const bowMaterial = new THREE.MeshStandardMaterial({ color: 0x8B4513 });
        const bow = new THREE.Mesh(bowGeometry, bowMaterial);
        bow.position.set(0.4, 0.9, 0);
        bow.rotation.z = Math.PI / 4;
        group.add(bow);
        
        return group;
    }
    
    createOrcMesh(group, baseColor, isAlly) {
        const skinColor = 0x2d5016;
        const skinMaterial = new THREE.MeshStandardMaterial({ 
            color: skinColor,
            roughness: 0.9,
            metalness: 0.1,
            emissive: isAlly ? 0x00ff00 : 0x000000,
            emissiveIntensity: isAlly ? 0.15 : 0
        });
        
        // Simplified orc mesh (same structure as enemy)
        const headGeometry = new THREE.BoxGeometry(0.65, 0.7, 0.6);
        const head = new THREE.Mesh(headGeometry, skinMaterial);
        head.position.set(0, 1.5, 0);
        group.add(head);
        
        const bodyGeometry = new THREE.BoxGeometry(0.75, 1.0, 0.55);
        const body = new THREE.Mesh(bodyGeometry, skinMaterial);
        body.position.set(0, 0.9, 0);
        group.add(body);
        
        const armGeometry = new THREE.BoxGeometry(0.28, 0.75, 0.28);
        const leftArm = new THREE.Mesh(armGeometry, skinMaterial);
        leftArm.position.set(-0.48, 0.85, 0);
        group.add(leftArm);
        
        const rightArm = new THREE.Mesh(armGeometry, skinMaterial);
        rightArm.position.set(0.48, 0.85, 0);
        group.add(rightArm);
        
        const legGeometry = new THREE.BoxGeometry(0.32, 0.75, 0.32);
        const leftLeg = new THREE.Mesh(legGeometry, skinMaterial);
        leftLeg.position.set(-0.22, 0.15, 0);
        group.add(leftLeg);
        
        const rightLeg = new THREE.Mesh(legGeometry, skinMaterial);
        rightLeg.position.set(0.22, 0.15, 0);
        group.add(rightLeg);
        
        // Club
        const clubGeometry = new THREE.BoxGeometry(0.18, 0.9, 0.18);
        const clubMaterial = new THREE.MeshStandardMaterial({ color: 0x654321 });
        const club = new THREE.Mesh(clubGeometry, clubMaterial);
        club.position.set(0.6, 0.85, 0);
        club.rotation.z = Math.PI / 6;
        group.add(club);
        
        return group;
    }
    
    createTrollMesh(group, baseColor, isAlly) {
        const skinColor = 0x6b4423;
        const skinMaterial = new THREE.MeshStandardMaterial({ 
            color: skinColor,
            roughness: 0.95,
            metalness: 0.05,
            emissive: isAlly ? 0x00ff00 : 0x000000,
            emissiveIntensity: isAlly ? 0.15 : 0
        });
        
        // Simplified troll mesh
        const headGeometry = new THREE.BoxGeometry(0.85, 0.9, 0.75);
        const head = new THREE.Mesh(headGeometry, skinMaterial);
        head.position.set(0, 1.65, 0);
        group.add(head);
        
        const bodyGeometry = new THREE.BoxGeometry(1.1, 1.3, 0.8);
        const body = new THREE.Mesh(bodyGeometry, skinMaterial);
        body.position.set(0, 0.75, 0);
        group.add(body);
        
        const armGeometry = new THREE.BoxGeometry(0.4, 0.6, 0.4);
        const leftArm = new THREE.Mesh(armGeometry, skinMaterial);
        leftArm.position.set(-0.65, 0.7, 0);
        group.add(leftArm);
        
        const rightArm = new THREE.Mesh(armGeometry, skinMaterial);
        rightArm.position.set(0.65, 0.7, 0);
        group.add(rightArm);
        
        const legGeometry = new THREE.BoxGeometry(0.45, 0.95, 0.45);
        const leftLeg = new THREE.Mesh(legGeometry, skinMaterial);
        leftLeg.position.set(-0.28, 0.0, 0);
        group.add(leftLeg);
        
        const rightLeg = new THREE.Mesh(legGeometry, skinMaterial);
        rightLeg.position.set(0.28, 0.0, 0);
        group.add(rightLeg);
        
        // Axe
        const axeHandleGeometry = new THREE.BoxGeometry(0.25, 1.3, 0.25);
        const axeHandleMaterial = new THREE.MeshStandardMaterial({ color: 0x3d2817 });
        const axeHandle = new THREE.Mesh(axeHandleGeometry, axeHandleMaterial);
        axeHandle.position.set(0.8, 0.35, 0);
        group.add(axeHandle);
        
        return group;
    }
    
    createWizardMesh(group, baseColor, isAlly) {
        // Wizard - mystical and powerful with staff
        const robeColor = 0x9D4EDD; // Purple robe
        const robeMaterial = new THREE.MeshStandardMaterial({ 
            color: robeColor,
            roughness: 0.7,
            emissive: isAlly ? 0x00ff00 : 0x000000,
            emissiveIntensity: isAlly ? 0.15 : 0
        });
        const skinColor = 0xffdbac; // Light skin
        const skinMaterial = new THREE.MeshStandardMaterial({ 
            color: skinColor,
            roughness: 0.7,
            emissive: isAlly ? 0x00ff00 : 0x000000,
            emissiveIntensity: isAlly ? 0.1 : 0
        });
        const hatColor = 0x6B0080; // Dark purple hat
        const hatMaterial = new THREE.MeshStandardMaterial({ 
            color: hatColor,
            roughness: 0.8,
            emissive: isAlly ? 0x00ff00 : 0x000000,
            emissiveIntensity: isAlly ? 0.1 : 0
        });
        
        // Head
        const headGeometry = new THREE.BoxGeometry(0.4, 0.4, 0.4);
        const head = new THREE.Mesh(headGeometry, skinMaterial);
        head.position.set(0, 1.5, 0);
        group.add(head);
        
        // Wizard hat (pointed)
        const hatGeometry = new THREE.ConeGeometry(0.3, 0.6, 8);
        const hat = new THREE.Mesh(hatGeometry, hatMaterial);
        hat.position.set(0, 1.9, 0);
        group.add(hat);
        
        // Beard (optional, makes wizard look older)
        const beardGeometry = new THREE.BoxGeometry(0.3, 0.2, 0.1);
        const beardMaterial = new THREE.MeshStandardMaterial({ color: 0xffffff, roughness: 0.9 });
        const beard = new THREE.Mesh(beardGeometry, beardMaterial);
        beard.position.set(0, 1.3, 0.2);
        group.add(beard);
        
        // Body (robe)
        const bodyGeometry = new THREE.BoxGeometry(0.6, 1.0, 0.5);
        const body = new THREE.Mesh(bodyGeometry, robeMaterial);
        body.position.set(0, 0.9, 0);
        group.add(body);
        
        // Arms (in robe sleeves)
        const armGeometry = new THREE.BoxGeometry(0.2, 0.7, 0.2);
        const leftArm = new THREE.Mesh(armGeometry, robeMaterial);
        leftArm.position.set(-0.4, 0.9, 0);
        group.add(leftArm);
        
        const rightArm = new THREE.Mesh(armGeometry, robeMaterial);
        rightArm.position.set(0.4, 0.9, 0);
        group.add(rightArm);
        
        // Legs (in robe)
        const legGeometry = new THREE.BoxGeometry(0.25, 0.7, 0.25);
        const leftLeg = new THREE.Mesh(legGeometry, robeMaterial);
        leftLeg.position.set(-0.15, 0.2, 0);
        group.add(leftLeg);
        
        const rightLeg = new THREE.Mesh(legGeometry, robeMaterial);
        rightLeg.position.set(0.15, 0.2, 0);
        group.add(rightLeg);
        
        // Wizard Staff (magical weapon)
        const staffHandleGeometry = new THREE.CylinderGeometry(0.03, 0.03, 1.2, 8);
        const staffHandleMaterial = new THREE.MeshStandardMaterial({ 
            color: 0x3d2817,
            roughness: 0.8
        });
        const staffHandle = new THREE.Mesh(staffHandleGeometry, staffHandleMaterial);
        staffHandle.position.set(0.5, 0.8, 0);
        staffHandle.rotation.z = Math.PI / 8;
        staffHandle.name = 'weapon';
        group.add(staffHandle);
        
        // Staff top (crystal/orb)
        const orbGeometry = new THREE.SphereGeometry(0.12, 16, 16);
        const orbMaterial = new THREE.MeshStandardMaterial({ 
            color: 0xFF00FF,
            emissive: 0xFF00FF,
            emissiveIntensity: 0.8,
            metalness: 0.9,
            roughness: 0.1
        });
        const orb = new THREE.Mesh(orbGeometry, orbMaterial);
        orb.position.set(0.5, 1.4, 0);
        orb.rotation.z = Math.PI / 8;
        group.add(orb);
        
        // Magical glow around orb
        const glowGeometry = new THREE.SphereGeometry(0.15, 16, 16);
        const glowMaterial = new THREE.MeshBasicMaterial({ 
            color: 0xFF00FF,
            transparent: true,
            opacity: 0.3
        });
        const glow = new THREE.Mesh(glowGeometry, glowMaterial);
        glow.position.set(0.5, 1.4, 0);
        glow.rotation.z = Math.PI / 8;
        group.add(glow);
        
        return group;
    }
    
    createHumanMesh(group, baseColor, isAlly) {
        const skinColor = 0xffdbac; // Skin tone
        const skinMaterial = new THREE.MeshStandardMaterial({ 
            color: skinColor,
            roughness: 0.8,
            emissive: isAlly ? 0x00ff00 : 0x000000,
            emissiveIntensity: isAlly ? 0.15 : 0
        });
        
        const armorColor = 0x888888; // Gray metal
        const armorMaterial = new THREE.MeshStandardMaterial({ 
            color: armorColor,
            metalness: 0.7,
            roughness: 0.3,
            emissive: isAlly ? 0x00ff00 : 0x000000,
            emissiveIntensity: isAlly ? 0.15 : 0
        });
        
        // Head (with face)
        const headGeometry = new THREE.BoxGeometry(0.5, 0.5, 0.5);
        const head = new THREE.Mesh(headGeometry, skinMaterial);
        head.position.set(0, 1.5, 0);
        group.add(head);
        
        // Face features - Eyes
        const eyeGeometry = new THREE.SphereGeometry(0.05, 8, 8);
        const eyeMaterial = new THREE.MeshStandardMaterial({ 
            color: 0x000000
        });
        
        const leftEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
        leftEye.position.set(-0.12, 1.52, 0.26);
        group.add(leftEye);
        
        const rightEye = new THREE.Mesh(eyeGeometry, eyeMaterial);
        rightEye.position.set(0.12, 1.52, 0.26);
        group.add(rightEye);
        
        // Nose
        const noseGeometry = new THREE.BoxGeometry(0.08, 0.1, 0.05);
        const nose = new THREE.Mesh(noseGeometry, skinMaterial);
        nose.position.set(0, 1.48, 0.26);
        group.add(nose);
        
        // Mouth
        const mouthGeometry = new THREE.BoxGeometry(0.15, 0.03, 0.02);
        const mouthMaterial = new THREE.MeshStandardMaterial({ 
            color: 0x8b0000
        });
        const mouth = new THREE.Mesh(mouthGeometry, mouthMaterial);
        mouth.position.set(0, 1.42, 0.25);
        group.add(mouth);
        
        // Helmet
        const helmetGeometry = new THREE.BoxGeometry(0.55, 0.4, 0.55);
        const helmet = new THREE.Mesh(helmetGeometry, armorMaterial);
        helmet.position.set(0, 1.65, 0);
        group.add(helmet);
        
        // Helmet visor/face guard
        const visorGeometry = new THREE.BoxGeometry(0.5, 0.15, 0.05);
        const visor = new THREE.Mesh(visorGeometry, armorMaterial);
        visor.position.set(0, 1.55, 0.23);
        group.add(visor);
        
        // Body (with armor)
        const bodyGeometry = new THREE.BoxGeometry(0.6, 0.9, 0.4);
        const body = new THREE.Mesh(bodyGeometry, skinMaterial);
        body.position.set(0, 0.9, 0);
        group.add(body);
        
        // Chest plate (armor)
        const chestPlateGeometry = new THREE.BoxGeometry(0.55, 0.5, 0.15);
        const chestPlate = new THREE.Mesh(chestPlateGeometry, armorMaterial);
        chestPlate.position.set(0, 1.05, 0.2);
        group.add(chestPlate);
        
        // Shoulder pads (armor)
        const shoulderGeometry = new THREE.BoxGeometry(0.3, 0.2, 0.3);
        const leftShoulder = new THREE.Mesh(shoulderGeometry, armorMaterial);
        leftShoulder.position.set(-0.35, 1.15, 0);
        group.add(leftShoulder);
        
        const rightShoulder = new THREE.Mesh(shoulderGeometry, armorMaterial);
        rightShoulder.position.set(0.35, 1.15, 0);
        group.add(rightShoulder);
        
        // Arms (with armor on upper arms)
        const upperArmGeometry = new THREE.BoxGeometry(0.22, 0.35, 0.22);
        const lowerArmGeometry = new THREE.BoxGeometry(0.18, 0.35, 0.18);
        
        // Left arm
        const leftUpperArm = new THREE.Mesh(upperArmGeometry, armorMaterial);
        leftUpperArm.position.set(-0.4, 1.0, 0);
        group.add(leftUpperArm);
        
        const leftLowerArm = new THREE.Mesh(lowerArmGeometry, skinMaterial);
        leftLowerArm.position.set(-0.4, 0.65, 0);
        group.add(leftLowerArm);
        
        // Right arm
        const rightUpperArm = new THREE.Mesh(upperArmGeometry, armorMaterial);
        rightUpperArm.position.set(0.4, 1.0, 0);
        group.add(rightUpperArm);
        
        const rightLowerArm = new THREE.Mesh(lowerArmGeometry, skinMaterial);
        rightLowerArm.position.set(0.4, 0.65, 0);
        group.add(rightLowerArm);
        
        // Legs (with armor on thighs)
        const thighGeometry = new THREE.BoxGeometry(0.27, 0.35, 0.27);
        const shinGeometry = new THREE.BoxGeometry(0.23, 0.35, 0.23);
        
        // Left leg
        const leftThigh = new THREE.Mesh(thighGeometry, armorMaterial);
        leftThigh.position.set(-0.18, 0.35, 0);
        group.add(leftThigh);
        
        const leftShin = new THREE.Mesh(shinGeometry, skinMaterial);
        leftShin.position.set(-0.18, 0.0, 0);
        group.add(leftShin);
        
        // Right leg
        const rightThigh = new THREE.Mesh(thighGeometry, armorMaterial);
        rightThigh.position.set(0.18, 0.35, 0);
        group.add(rightThigh);
        
        const rightShin = new THREE.Mesh(shinGeometry, skinMaterial);
        rightShin.position.set(0.18, 0.0, 0);
        group.add(rightShin);
        
        // Sword
        const swordBladeGeometry = new THREE.BoxGeometry(0.08, 0.7, 0.02);
        const swordBladeMaterial = new THREE.MeshStandardMaterial({ 
            color: 0xcccccc,
            metalness: 0.8,
            roughness: 0.2
        });
        const swordBlade = new THREE.Mesh(swordBladeGeometry, swordBladeMaterial);
        swordBlade.position.set(0.5, 0.9, 0);
        swordBlade.rotation.z = Math.PI / 8;
        group.add(swordBlade);
        
        const swordTipGeometry = new THREE.ConeGeometry(0.04, 0.1, 4);
        const swordTip = new THREE.Mesh(swordTipGeometry, swordBladeMaterial);
        swordTip.rotation.x = Math.PI / 2;
        swordTip.position.set(0.5, 1.25, 0);
        swordTip.rotation.z = Math.PI / 8;
        group.add(swordTip);
        
        const guardGeometry = new THREE.BoxGeometry(0.2, 0.05, 0.05);
        const guardMaterial = new THREE.MeshStandardMaterial({ 
            color: 0x888888,
            metalness: 0.6,
            roughness: 0.4
        });
        const guard = new THREE.Mesh(guardGeometry, guardMaterial);
        guard.position.set(0.5, 0.65, 0);
        guard.rotation.z = Math.PI / 8;
        group.add(guard);
        
        const hiltGeometry = new THREE.BoxGeometry(0.06, 0.15, 0.06);
        const hiltMaterial = new THREE.MeshStandardMaterial({ 
            color: 0x654321,
            roughness: 0.8
        });
        const hilt = new THREE.Mesh(hiltGeometry, hiltMaterial);
        hilt.position.set(0.5, 0.55, 0);
        hilt.rotation.z = Math.PI / 8;
        group.add(hilt);
        
        const pommelGeometry = new THREE.SphereGeometry(0.05, 8, 8);
        const pommelMaterial = new THREE.MeshStandardMaterial({ 
            color: 0x666666,
            metalness: 0.7,
            roughness: 0.3
        });
        const pommel = new THREE.Mesh(pommelGeometry, pommelMaterial);
        pommel.position.set(0.5, 0.45, 0);
        pommel.rotation.z = Math.PI / 8;
        group.add(pommel);
        
        return group;
    }
    
    update(deltaTime) {
        if (this.isDead || !this.player) return;
        
        // Find nearest enemy target
        this.targetSearchTimer += deltaTime;
        if (this.targetSearchTimer >= this.targetSearchInterval) {
            this.findNearestEnemy();
            this.targetSearchTimer = 0;
        }
        
        // If no target, follow player
        if (!this.currentTarget || this.currentTarget.isDead) {
            this.followPlayer(deltaTime);
            return;
        }
        
        // Move towards target enemy
        const direction = new THREE.Vector3()
            .subVectors(this.currentTarget.position, this.position);
        direction.y = 0;
        
        if (direction.length() > 0.1) {
            direction.normalize();
            
            const moveVector = direction.multiplyScalar(this.moveSpeed);
            this.body.velocity.x = moveVector.x;
            this.body.velocity.z = moveVector.z;
            
            // Boundary check - prevent ally from leaving the ground area
            if (this.body.position.x > this.groundBoundary) {
                this.body.position.x = this.groundBoundary;
                this.body.velocity.x = 0;
            }
            if (this.body.position.x < -this.groundBoundary) {
                this.body.position.x = -this.groundBoundary;
                this.body.velocity.x = 0;
            }
            if (this.body.position.z > this.groundBoundary) {
                this.body.position.z = this.groundBoundary;
                this.body.velocity.z = 0;
            }
            if (this.body.position.z < -this.groundBoundary) {
                this.body.position.z = -this.groundBoundary;
                this.body.velocity.z = 0;
            }
            
            // Sync mesh with physics body after boundary check
            this.mesh.position.x = this.body.position.x;
            this.mesh.position.y = this.body.position.y;
            this.mesh.position.z = this.body.position.z;
            
            // Look at target
            this.mesh.lookAt(this.currentTarget.position);
        }
        
        // Attack enemy if in range
        const distanceToTarget = this.position.distanceTo(this.currentTarget.position);
        if (distanceToTarget <= this.attackRange) {
            // Stop moving
            this.body.velocity.x = 0;
            this.body.velocity.z = 0;
            
            // Attack enemy
            const currentTime = Date.now() / 1000;
            if (currentTime - this.lastAttackTime >= this.attackCooldown) {
                this.attackEnemy(this.currentTarget);
                this.lastAttackTime = currentTime;
            }
        }
        
        // Update blood particles
        this.bloodSystem.update(deltaTime);
    }
    
    findNearestEnemy() {
        let nearestEnemy = null;
        let nearestDistance = Infinity;
        
        // Find closest alive enemy
        for (const enemy of this.enemies) {
            if (enemy.isDead || !enemy.mesh) continue;
            
            const distance = this.position.distanceTo(enemy.position);
            if (distance < nearestDistance && distance < 30) { // Max engagement range
                nearestDistance = distance;
                nearestEnemy = enemy;
            }
        }
        
        this.currentTarget = nearestEnemy;
    }
    
    followPlayer(deltaTime) {
        // Follow player at a distance
        const direction = new THREE.Vector3()
            .subVectors(this.player.camera.position, this.position);
        direction.y = 0;
        
        const distance = direction.length();
        
        // Keep some distance from player (don't crowd)
        if (distance > 5) {
            direction.normalize();
            const moveVector = direction.multiplyScalar(this.moveSpeed);
            this.body.velocity.x = moveVector.x;
            this.body.velocity.z = moveVector.z;
        } else {
            // Too close, stop
            this.body.velocity.x = 0;
            this.body.velocity.z = 0;
        }
        
        // Sync mesh with physics body
        // Boundary check - prevent ally from leaving the ground area
        if (this.body.position.x > this.groundBoundary) {
            this.body.position.x = this.groundBoundary;
            this.body.velocity.x = 0;
        }
        if (this.body.position.x < -this.groundBoundary) {
            this.body.position.x = -this.groundBoundary;
            this.body.velocity.x = 0;
        }
        if (this.body.position.z > this.groundBoundary) {
            this.body.position.z = this.groundBoundary;
            this.body.velocity.z = 0;
        }
        if (this.body.position.z < -this.groundBoundary) {
            this.body.position.z = -this.groundBoundary;
            this.body.velocity.z = 0;
        }
        
        // Sync mesh with physics body after boundary check
        // Boundary check - prevent ally from leaving the ground area
        if (this.body.position.x > this.groundBoundary) {
            this.body.position.x = this.groundBoundary;
            this.body.velocity.x = 0;
        }
        if (this.body.position.x < -this.groundBoundary) {
            this.body.position.x = -this.groundBoundary;
            this.body.velocity.x = 0;
        }
        if (this.body.position.z > this.groundBoundary) {
            this.body.position.z = this.groundBoundary;
            this.body.velocity.z = 0;
        }
        if (this.body.position.z < -this.groundBoundary) {
            this.body.position.z = -this.groundBoundary;
            this.body.velocity.z = 0;
        }
        
        // Sync mesh with physics body after boundary check
        this.mesh.position.x = this.body.position.x;
        this.mesh.position.y = this.body.position.y;
        this.mesh.position.z = this.body.position.z;
        
        // Look at player
        this.mesh.lookAt(this.player.camera.position);
    }
    
    attackEnemy(enemy) {
        if (!enemy || enemy.isDead || this.isDead) return;
        
        this.isAttacking = true;
        
        // Apply damage to enemy
        const damage = this.attackDamage + Math.random() * 3;
        const hitPosition = enemy.position.clone();
        const hitDirection = new THREE.Vector3()
            .subVectors(hitPosition, this.position)
            .normalize();
        
        enemy.takeDamage(damage, hitPosition, hitDirection);
        
        // Play attack sound
        audioManager.playSound('enemy_attack', {
            position: this.position,
            volume: 0.4
        });
        
        // Reset attack flag
        setTimeout(() => {
            this.isAttacking = false;
        }, 200);
    }
    
    takeDamage(amount, hitPosition, hitDirection) {
        this.health -= amount;
        this.isWounded = true;
        
        // Create blood splatter
        if (hitPosition && hitDirection) {
            this.bloodSystem.createBloodSplatter(
                hitPosition,
                hitDirection,
                Math.min(amount / 10, 10)
            );
        }
        
        if (this.health <= 0) {
            this.die(hitPosition, hitDirection);
        }
    }
    
    die(hitPosition, hitDirection) {
        if (this.isDead) return;
        
        this.isDead = true;
        
        // Blood splatter on death
        if (hitPosition && hitDirection) {
            this.bloodSystem.createBloodSplatter(
                hitPosition,
                hitDirection,
                10
            );
        }
        
        // Remove from scene and physics world
        if (this.mesh) {
            this.mesh.traverse((child) => {
                if (child.isMesh) {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) {
                        if (Array.isArray(child.material)) {
                            child.material.forEach(mat => mat.dispose());
                        } else {
                            child.material.dispose();
                        }
                    }
                }
            });
            this.scene.remove(this.mesh);
        }
        if (this.body) {
            this.world.removeBody(this.body);
        }
    }
    
    get position() {
        return this.mesh.position;
    }
    
    set position(value) {
        this.mesh.position.copy(value);
        if (this.body) {
            this.body.position.set(value.x, value.y, value.z);
        }
    }
}

